import React from "react";
import { Box, Typography, Button } from "@mui/material";
import "./PeripheralDevicesDisplay.css";

/**
 * Helper function to format device list for display
 */
const formatDeviceListForDisplay = (devices) => {
  return Array.isArray(devices) ? devices : [];
};

/**
 * Helper function to get device severity info
 */
const getDeviceSeverityInfo = (severity) => ({
  color: severity === "high" ? "#d32f2f" : "#ff9800",
});

/**
 * Peripheral Devices Display Component
 * Handles peripheral device detection UI and error messages
 */
export const PeripheralDevicesDisplay = ({
  hasViolatingDevices,
  detectedDevices,
  peripheralError,
  isCheckingDevices,
  isPeripheralRestrictionRequired,
  onReCheck,
  onRequestPermissions,
}) => {
  if (!isPeripheralRestrictionRequired) {
    return null;
  }

  if (!hasViolatingDevices && !peripheralError) {
    return null;
  }

  return (
    <Box className="peripheral-alert-container">
      <Box className="peripheral-alert-header">
        <Box className="alert-icon-wrapper">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z"
              fill="#d32f2f"
            />
          </svg>
        </Box>
        <Box className="alert-content">
          {/* Show permission error if present */}
          {peripheralError?.name === "PeripheralPermissionDenied" && (
            <>
              <Typography className="alert-title">
                🔒 Peripheral Device Detection Failed
              </Typography>
              <Typography className="alert-message">
                {peripheralError.message}
              </Typography>
              <Box className="alert-actions">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onRequestPermissions}
                  disabled={isCheckingDevices}
                  className="alert-button primary"
                >
                  {isCheckingDevices
                    ? "Requesting Permissions..."
                    : "🔑 Grant Permissions & Re-check"}
                </Button>
              </Box>
            </>
          )}

          {/* Show device violations if present and no permission error */}
          {hasViolatingDevices &&
            !peripheralError?.name?.includes("Permission") && (
              <>
                <Typography className="alert-title">
                  🚫 External Cameras or Displays Detected
                </Typography>
                <Typography className="alert-message">
                  The following external cameras or displays were detected and
                  must be disconnected:
                </Typography>

                {/* Display detected devices */}
                <Box className="device-list">
                  {formatDeviceListForDisplay(detectedDevices).map(
                    (device, index) => {
                      const severityInfo = getDeviceSeverityInfo(
                        device.severity
                      );
                      return (
                        <Box key={index} className="device-item">
                          <Box
                            className="device-indicator"
                            sx={{ bgcolor: severityInfo.color }}
                          />
                          <Typography className="device-name">
                            {device.name}
                          </Typography>
                          <Typography className="device-severity">
                            {device.severity}
                          </Typography>
                        </Box>
                      );
                    }
                  )}
                </Box>

                <Box className="alert-actions">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onReCheck}
                    disabled={isCheckingDevices}
                    className="alert-button primary"
                  >
                    {isCheckingDevices ? "Checking..." : "Re-check Devices"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onRequestPermissions}
                    disabled={isCheckingDevices}
                    className="alert-button secondary"
                  >
                    🔑 Grant Permissions
                  </Button>
                </Box>
              </>
            )}

          {/* Show generic peripheral error if present */}
          {peripheralError &&
            !peripheralError?.name?.includes("Permission") &&
            !hasViolatingDevices && (
              <>
                <Typography className="alert-title">
                  ⚠️ Peripheral Device Error
                </Typography>
                <Typography className="alert-message">
                  {peripheralError.message}
                </Typography>
                <Box className="alert-actions">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onReCheck}
                    disabled={isCheckingDevices}
                    className="alert-button primary"
                  >
                    {isCheckingDevices ? "Checking..." : "Retry Detection"}
                  </Button>
                </Box>
              </>
            )}
        </Box>
      </Box>
    </Box>
  );
};
